/**
 * Gantt activity realization — derive actual start/end per auto-planned activity
 * from polygon object phase history (first related work in group starts realization).
 */

import type { GanttPlanningActivityId } from '../constants/gantt-planning.constants';
import { GANTT_PLANNING_ACTIVITY_IDS } from '../constants/gantt-planning.constants';
import { isAcquisitionClosed } from '../constants/acquisition-report.constants';
import {
  countGanttPlanningZavrtBuildings,
  type GanttPlanningObjectInput,
} from './gantt-planning.util';
import {
  getRequiredMilestones,
  isBudovaWaitingForProgressMilestone,
  isMilestoneComplete,
  parseBudovaMilestonesFromMetadata,
  parseBudovaMilestonesMeta,
  type BudovaMilestoneId,
} from '../constants/object-workflow/budova-milestone-engine';
import { inferBudovaBuildingType } from '../constants/object-workflow/budova-milestone-engine';
import {
  isRozvadecMilestoneComplete,
  parseRozvadecMilestonesFromMetadata,
  parseRozvadecMilestonesMeta,
  ROZVADEC_MILESTONE_IDS,
  ROZVADEC_REQUIRED_MILESTONES,
  type RozvadecMilestoneId,
} from '../constants/object-workflow/rozvadec-milestone-engine';
import {
  isRozvadecZafukKind,
  isRozvadecZafukMicrotubeKind,
  normalizeRozvadecZafukEntries,
  type RozvadecZafukKind,
} from '../constants/object-workflow/rozvadec-zafuk.constants';
import { normalizeObjectTypeForLookup } from '../constants/object-workflow/phase-codes';
import { getPhaseByCode, getPhaseInfoByCode, hasWorkflow } from '../constants/object-workflow/workflow-model';
import { formatGanttPlanningDate, parseGanttPlanningDate } from './gantt-planning.util';

export interface GanttRealizationObjectInput {
  id?: string;
  type: string;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  updatedAt?: string | Date | null;
  acquisitionStatusAcq?: string | null;
}

export interface GanttRealizationHistoryEvent {
  objectId: string;
  objectType: string;
  occurredAt: string;
  statusFrom?: string | null;
  statusTo?: string | null;
  newValues?: Record<string, unknown> | null;
}

export interface GanttActivityRealization {
  activityId: GanttPlanningActivityId;
  /** `dd.mm.yyyy` when first related work started; empty when not started. */
  realizedFrom: string;
  /** `dd.mm.yyyy` when group is complete; empty while in progress. */
  realizedTo: string;
}

export interface DeriveGanttActivityRealizationsInput {
  objects: ReadonlyArray<GanttRealizationObjectInput>;
  history: ReadonlyArray<GanttRealizationHistoryEvent>;
  polygonHasAcquisitionImport?: boolean;
}

type ZafukActivityBucket = 'zafuk_ts' | 'zafuk_prim_ok' | 'zafuk_sek_ok';

function normalizeType(type: string): string {
  return normalizeObjectTypeForLookup((type ?? '').trim());
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

function minDate(dates: Array<Date | null | undefined>): Date | null {
  let min: Date | null = null;
  for (const d of dates) {
    if (!d) continue;
    if (!min || d.getTime() < min.getTime()) min = d;
  }
  return min;
}

function maxDate(dates: Array<Date | null | undefined>): Date | null {
  let max: Date | null = null;
  for (const d of dates) {
    if (!d) continue;
    if (!max || d.getTime() > max.getTime()) max = d;
  }
  return max;
}

function formatRealizationDate(date: Date | null): string {
  if (!date) return '';
  return formatGanttPlanningDate(date);
}

function metadataFromNewValues(newValues: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!newValues || typeof newValues !== 'object') return null;
  const meta = newValues['metadata'];
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) return meta as Record<string, unknown>;
  const custom = newValues['customFields'] ?? newValues['custom_fields'];
  if (custom && typeof custom === 'object' && !Array.isArray(custom)) {
    return { customFields: custom };
  }
  return null;
}

function mergeMetadata(
  base: Record<string, unknown> | null | undefined,
  patch: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(base ?? {}) };
  if (!patch) return out;
  for (const [k, v] of Object.entries(patch)) {
    if (v && typeof v === 'object' && !Array.isArray(v) && out[k] && typeof out[k] === 'object' && !Array.isArray(out[k])) {
      out[k] = { ...(out[k] as Record<string, unknown>), ...(v as Record<string, unknown>) };
    } else {
      out[k] = v;
    }
  }
  return out;
}

function classifyZafukTextBucket(text: string): ZafukActivityBucket | null {
  const n = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!n) return null;
  if (n.includes('sekund')) return 'zafuk_sek_ok';
  if (n.includes('prim')) return 'zafuk_prim_ok';
  if (/\bts\b/.test(n) || n.includes('mikrotrub') || (n.includes('zafuk') && n.includes('mt'))) {
    return 'zafuk_ts';
  }
  return null;
}

function classifyZafukBucketFromKind(kind: RozvadecZafukKind, typOk?: string | null): ZafukActivityBucket {
  if (isRozvadecZafukMicrotubeKind(kind)) return 'zafuk_ts';
  const fromTyp = classifyZafukTextBucket(typOk ?? '');
  return fromTyp ?? 'zafuk_prim_ok';
}

function zafukBucketsInMetadata(metadata: Record<string, unknown> | null | undefined): Set<ZafukActivityBucket> {
  const buckets = new Set<ZafukActivityBucket>();
  if (!metadata) return buckets;

  const entries = normalizeRozvadecZafukEntries({ metadata });
  for (const entry of entries) {
    if (!isRozvadecZafukKind(entry.kind)) continue;
    buckets.add(classifyZafukBucketFromKind(entry.kind, entry.typOk));
  }

  const spec = metadata['spec'];
  if (spec && typeof spec === 'object' && !Array.isArray(spec)) {
    const specObj = spec as Record<string, unknown>;
    const zafukTyp = String(specObj['zafukTypOk'] ?? specObj['zafuk_typ_ok'] ?? '').trim();
    const bucket = classifyZafukTextBucket(zafukTyp);
    if (bucket) buckets.add(bucket);
  }

  const flags = parseBudovaMilestonesFromMetadata(metadata);
  if (flags.zafouknuto) buckets.add('zafuk_prim_ok');

  return buckets;
}

function phaseIndexAtLeast(phaseCode: string | null | undefined, minIndex: number): boolean {
  const info = getPhaseInfoByCode(phaseCode ?? '');
  if (!info) return false;
  return info.phase.index >= minIndex;
}

function isInitialPhase(objectType: string, phaseCode: string | null | undefined): boolean {
  const phase = getPhaseByCode(objectType, phaseCode ?? '');
  return phase?.isInitial === true;
}

function isFinalPhase(objectType: string, phaseCode: string | null | undefined): boolean {
  const phase = getPhaseByCode(objectType, phaseCode ?? '');
  return phase?.isFinal === true;
}

function budovaMilestoneJustCompleted(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  milestoneId: BudovaMilestoneId,
): boolean {
  const beforeFlags = parseBudovaMilestonesFromMetadata(before);
  const afterFlags = parseBudovaMilestonesFromMetadata(after);
  const beforeComplete = beforeFlags[milestoneIdToFlagKey(milestoneId)] === true;
  const afterComplete = afterFlags[milestoneIdToFlagKey(milestoneId)] === true;
  if (!beforeComplete && afterComplete) return true;
  const beforeAt = parseBudovaMilestonesMeta(before).completedAt[milestoneId];
  const afterAt = parseBudovaMilestonesMeta(after).completedAt[milestoneId];
  return !beforeAt && !!afterAt;
}

function rozvadecMilestoneJustCompleted(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  milestoneId: RozvadecMilestoneId,
): boolean {
  const beforeFlags = parseRozvadecMilestonesFromMetadata(before);
  const afterFlags = parseRozvadecMilestonesFromMetadata(after);
  const beforeComplete = isRozvadecMilestoneComplete(milestoneId, beforeFlags);
  const afterComplete = isRozvadecMilestoneComplete(milestoneId, afterFlags);
  if (!beforeComplete && afterComplete) return true;
  const beforeAt = parseRozvadecMilestonesMeta(before).completedAt[milestoneId];
  const afterAt = parseRozvadecMilestonesMeta(after).completedAt[milestoneId];
  return !beforeAt && !!afterAt;
}

function rozvadecWeldingEventMatches(
  metaBefore: Record<string, unknown> | null,
  metaAfter: Record<string, unknown> | null,
): boolean {
  return ROZVADEC_MILESTONE_IDS.some((id) => rozvadecMilestoneJustCompleted(metaBefore, metaAfter, id));
}

function rozvadecWeldingStarted(metadata: Record<string, unknown> | null | undefined): boolean {
  const flags = parseRozvadecMilestonesFromMetadata(metadata);
  return ROZVADEC_REQUIRED_MILESTONES.some((id) => isRozvadecMilestoneComplete(id, flags));
}

function milestoneIdToFlagKey(id: BudovaMilestoneId): keyof ReturnType<typeof parseBudovaMilestonesFromMetadata> {
  switch (id) {
    case 'ZAVRTANY':
      return 'zavrtany';
    case 'ZRIZENE_VR':
      return 'zrizeneVr';
    case 'ODBOCENO':
      return 'odboceno';
    case 'POSTAVENA_BABOVKA':
      return 'postavenaBabovka';
    case 'ZAFOUKNUTO':
      return 'zafouknuto';
    default:
      return 'zavrtany';
  }
}

function eventMatchesActivity(
  activityId: GanttPlanningActivityId,
  event: GanttRealizationHistoryEvent,
  metaBefore: Record<string, unknown> | null,
  metaAfter: Record<string, unknown> | null,
): boolean {
  const type = normalizeType(event.objectType);
  const occurred = toDate(event.occurredAt);
  if (!occurred) return false;

  switch (activityId) {
    case 'vykop':
      if (type !== 'VYKOP' && type !== 'JAMA') return false;
      return (
        isInitialPhase(type, event.statusFrom) &&
        !!event.statusTo &&
        !isInitialPhase(type, event.statusTo)
      );

    case 'zavrt':
      if (type !== 'BUDOVA') return false;
      return budovaMilestoneJustCompleted(metaBefore, metaAfter, 'ZAVRTANY');

    case 'odbocka':
      if (type === 'SPOJKOVISTE') {
        return (
          isInitialPhase(type, event.statusFrom) &&
          !!event.statusTo &&
          !isInitialPhase(type, event.statusTo)
        );
      }
      if (type === 'BUDOVA') {
        return budovaMilestoneJustCompleted(metaBefore, metaAfter, 'ODBOCENO');
      }
      return false;

    case 'rozvadec':
      if (type !== 'ROZVADEC') return false;
      return rozvadecWeldingEventMatches(metaBefore, metaAfter);

    case 'zafuk_ts':
    case 'zafuk_prim_ok':
    case 'zafuk_sek_ok': {
      const beforeBuckets = zafukBucketsInMetadata(metaBefore);
      const afterBuckets = zafukBucketsInMetadata(metaAfter);
      if (!afterBuckets.has(activityId)) return false;
      return !beforeBuckets.has(activityId);
    }

    default:
      return false;
  }
}

function zavrtBuildings(
  objects: ReadonlyArray<GanttRealizationObjectInput>,
  polygonHasAcquisitionImport: boolean,
): GanttRealizationObjectInput[] {
  const budovy = objects.filter((o) => normalizeType(o.type) === 'BUDOVA');
  if (!polygonHasAcquisitionImport) return budovy;
  const closed = budovy.filter((o) => isAcquisitionClosed(o.acquisitionStatusAcq ?? null));
  return closed.length > 0 ? closed : budovy;
}

/** BUDOVA ve scope montáže odboček (milník ODBOCENO ve workflow typu budovy). */
function odbockaBudovaBuildings(
  objects: ReadonlyArray<GanttRealizationObjectInput>,
  polygonHasAcquisitionImport: boolean,
): GanttRealizationObjectInput[] {
  return zavrtBuildings(objects, polygonHasAcquisitionImport).filter((o) => {
    const buildingType = inferBudovaBuildingType({ metadata: o.metadata ?? null, status: o.status ?? null });
    return getRequiredMilestones(buildingType).includes('ODBOCENO');
  });
}

function odbockaScopeObjects(
  objects: ReadonlyArray<GanttRealizationObjectInput>,
  polygonHasAcquisitionImport: boolean,
): GanttRealizationObjectInput[] {
  const spojky = objects.filter((o) => normalizeType(o.type) === 'SPOJKOVISTE');
  return [...spojky, ...odbockaBudovaBuildings(objects, polygonHasAcquisitionImport)];
}

function isActivityComplete(
  activityId: GanttPlanningActivityId,
  objects: ReadonlyArray<GanttRealizationObjectInput>,
  polygonHasAcquisitionImport: boolean,
): boolean {
  switch (activityId) {
    case 'vykop': {
      const rows = objects.filter((o) => {
        const t = normalizeType(o.type);
        return t === 'VYKOP' || t === 'JAMA';
      });
      if (rows.length === 0) return false;
      return rows.every((o) => isFinalPhase(o.type, o.status));
    }
    case 'zavrt': {
      const budovy = zavrtBuildings(objects, polygonHasAcquisitionImport);
      if (budovy.length === 0) return false;
      return budovy.every((o) => {
        const buildingType = inferBudovaBuildingType({ metadata: o.metadata ?? null, status: o.status ?? null });
        const flags = parseBudovaMilestonesFromMetadata(o.metadata ?? null);
        return !isBudovaWaitingForProgressMilestone(buildingType, 'ZAVRTANY', o.status, {
          milestones: flags,
        });
      });
    }
    case 'odbocka': {
      const scope = odbockaScopeObjects(objects, polygonHasAcquisitionImport);
      if (scope.length === 0) return false;
      return scope.every((o) => {
        const t = normalizeType(o.type);
        if (t === 'SPOJKOVISTE') return !isInitialPhase(t, o.status);
        if (t === 'BUDOVA') {
          const buildingType = inferBudovaBuildingType({ metadata: o.metadata ?? null, status: o.status ?? null });
          const flags = parseBudovaMilestonesFromMetadata(o.metadata ?? null);
          return !isBudovaWaitingForProgressMilestone(buildingType, 'ODBOCENO', o.status, {
            milestones: flags,
          });
        }
        return false;
      });
    }
    case 'rozvadec': {
      const rows = objects.filter((o) => normalizeType(o.type) === 'ROZVADEC');
      if (rows.length === 0) return false;
      return rows.every((o) => isFinalPhase(o.type, o.status));
    }
    case 'zafuk_ts':
    case 'zafuk_prim_ok':
    case 'zafuk_sek_ok': {
      const scoped = objects.filter((o) => zafukBucketsInMetadata(o.metadata ?? null).has(activityId));
      if (scoped.length === 0) return false;
      return scoped.every((o) => {
        const t = normalizeType(o.type);
        if (t === 'BUDOVA') {
          const buildingType = inferBudovaBuildingType({ metadata: o.metadata ?? null, status: o.status ?? null });
          const flags = parseBudovaMilestonesFromMetadata(o.metadata ?? null);
          return isMilestoneComplete(buildingType, 'ZAFOUKNUTO', { milestones: flags });
        }
        if (t === 'ROZVADEC') {
          if (activityId === 'zafuk_ts') return phaseIndexAtLeast(o.status, 2);
          const weldFlags = parseRozvadecMilestonesFromMetadata(o.metadata ?? null);
          return isRozvadecMilestoneComplete('PROVARENI', weldFlags) || phaseIndexAtLeast(o.status, 3);
        }
        return false;
      });
    }
    default:
      return false;
  }
}

function activityCompletionEndDate(
  activityId: GanttPlanningActivityId,
  objects: ReadonlyArray<GanttRealizationObjectInput>,
  polygonHasAcquisitionImport: boolean,
): Date | null {
  if (!isActivityComplete(activityId, objects, polygonHasAcquisitionImport)) return null;
  const scoped: GanttRealizationObjectInput[] = [];
  switch (activityId) {
    case 'vykop':
      scoped.push(...objects.filter((o) => {
        const t = normalizeType(o.type);
        return t === 'VYKOP' || t === 'JAMA';
      }));
      break;
    case 'zavrt':
      scoped.push(...zavrtBuildings(objects, polygonHasAcquisitionImport));
      break;
    case 'odbocka':
      scoped.push(...odbockaScopeObjects(objects, polygonHasAcquisitionImport));
      break;
    case 'rozvadec':
      scoped.push(...objects.filter((o) => normalizeType(o.type) === 'ROZVADEC'));
      break;
    case 'zafuk_ts':
    case 'zafuk_prim_ok':
    case 'zafuk_sek_ok':
      scoped.push(...objects.filter((o) => zafukBucketsInMetadata(o.metadata ?? null).has(activityId)));
      break;
    default:
      break;
  }
  return maxDate(scoped.map((o) => toDate(o.updatedAt)));
}

function fallbackStartFromObjects(
  activityId: GanttPlanningActivityId,
  objects: ReadonlyArray<GanttRealizationObjectInput>,
  polygonHasAcquisitionImport: boolean,
): Date | null {
  const candidates: Date[] = [];
  for (const o of objects) {
    const t = normalizeType(o.type);
    if (!hasWorkflow(t)) continue;
    const updated = toDate(o.updatedAt);
    if (!updated) continue;

    switch (activityId) {
      case 'vykop':
        if ((t === 'VYKOP' || t === 'JAMA') && !isInitialPhase(t, o.status)) candidates.push(updated);
        break;
      case 'zavrt': {
        if (t !== 'BUDOVA') break;
        const buildingType = inferBudovaBuildingType({ metadata: o.metadata ?? null, status: o.status ?? null });
        const flags = parseBudovaMilestonesFromMetadata(o.metadata ?? null);
        if (isMilestoneComplete(buildingType, 'ZAVRTANY', { milestones: flags })) candidates.push(updated);
        break;
      }
      case 'odbocka':
        if (t === 'SPOJKOVISTE' && !isInitialPhase(t, o.status)) candidates.push(updated);
        if (t === 'BUDOVA') {
          const buildingType = inferBudovaBuildingType({ metadata: o.metadata ?? null, status: o.status ?? null });
          const flags = parseBudovaMilestonesFromMetadata(o.metadata ?? null);
          if (isMilestoneComplete(buildingType, 'ODBOCENO', { milestones: flags })) candidates.push(updated);
        }
        break;
      case 'rozvadec':
        if (t === 'ROZVADEC' && rozvadecWeldingStarted(o.metadata ?? null)) candidates.push(updated);
        break;
      case 'zafuk_ts':
      case 'zafuk_prim_ok':
      case 'zafuk_sek_ok':
        if ((t === 'ROZVADEC' || t === 'BUDOVA') && zafukBucketsInMetadata(o.metadata ?? null).has(activityId)) {
          candidates.push(updated);
        }
        break;
      default:
        break;
    }
  }

  if (activityId === 'zavrt') {
    const budovy = zavrtBuildings(objects, polygonHasAcquisitionImport);
    if (budovy.length === 0) return null;
  }

  return minDate(candidates);
}

/** Derive per-activity realization dates from object snapshots + chronological history. */
export function deriveGanttActivityRealizations(
  input: DeriveGanttActivityRealizationsInput,
): Record<GanttPlanningActivityId, GanttActivityRealization> {
  const polygonHasAcquisitionImport = input.polygonHasAcquisitionImport === true;
  const objects = input.objects ?? [];
  const metaByObject = new Map<string, Record<string, unknown>>();
  for (const o of objects) {
    if (o.id) metaByObject.set(o.id, { ...(o.metadata ?? {}) });
  }

  const sortedHistory = [...(input.history ?? [])].sort(
    (a, b) => (toDate(a.occurredAt)?.getTime() ?? 0) - (toDate(b.occurredAt)?.getTime() ?? 0),
  );

  const firstEventAt = new Map<GanttPlanningActivityId, Date>();
  const lastEventAt = new Map<GanttPlanningActivityId, Date>();

  for (const event of sortedHistory) {
    const metaBefore = metaByObject.get(event.objectId) ?? null;
    const patch = metadataFromNewValues(event.newValues);
    const metaAfter = mergeMetadata(metaBefore, patch);
    if (event.objectId) metaByObject.set(event.objectId, metaAfter);

    const at = toDate(event.occurredAt);
    if (!at) continue;

    for (const activityId of GANTT_PLANNING_ACTIVITY_IDS) {
      if (!eventMatchesActivity(activityId, event, metaBefore, metaAfter)) continue;
      if (!firstEventAt.has(activityId)) firstEventAt.set(activityId, at);
      lastEventAt.set(activityId, at);
    }

    if (event.statusTo) {
      const type = normalizeType(event.objectType);
      if (hasWorkflow(type)) {
        const current = metaByObject.get(event.objectId) ?? {};
        metaByObject.set(event.objectId, current);
      }
    }
  }

  const out = {} as Record<GanttPlanningActivityId, GanttActivityRealization>;
  for (const activityId of GANTT_PLANNING_ACTIVITY_IDS) {
    const started =
      firstEventAt.get(activityId) ?? fallbackStartFromObjects(activityId, objects, polygonHasAcquisitionImport);
    const complete = started ? isActivityComplete(activityId, objects, polygonHasAcquisitionImport) : false;
    const ended = complete
      ? maxDate([lastEventAt.get(activityId), activityCompletionEndDate(activityId, objects, polygonHasAcquisitionImport)])
      : null;

    out[activityId] = {
      activityId,
      realizedFrom: formatRealizationDate(started),
      realizedTo: complete ? formatRealizationDate(ended) : '',
    };
  }
  return out;
}

/** Merge derived realization with optional manual override dates (manual wins when set). */
export function resolveGanttActivityRealizationDates(
  activityId: GanttPlanningActivityId,
  derived: GanttActivityRealization | null | undefined,
  manualFrom?: string | null,
  manualTo?: string | null,
): { realizedFrom: string; realizedTo: string } {
  const dFrom = (derived?.realizedFrom ?? '').trim();
  const dTo = (derived?.realizedTo ?? '').trim();
  const mFrom = (manualFrom ?? '').trim();
  const mTo = (manualTo ?? '').trim();

  const realizedFrom = mFrom || dFrom;
  let realizedTo = mTo || dTo;
  if (realizedFrom && realizedTo) {
    const from = parseGanttPlanningDate(realizedFrom);
    const to = parseGanttPlanningDate(realizedTo);
    if (from && to && to.getTime() < from.getTime()) {
      realizedTo = realizedFrom;
    }
  }
  return { realizedFrom, realizedTo };
}

/** Whether zavrt scope exists for planning (used to skip empty activities in UI). */
export function countGanttPlanningZavrtFromRealizationObjects(
  objects: ReadonlyArray<GanttPlanningObjectInput>,
  polygonHasAcquisitionImport: boolean,
): number {
  return countGanttPlanningZavrtBuildings(objects, polygonHasAcquisitionImport);
}

/** ROZVADEC welding milestones complete — helper for rozvadec activity end signal. */
export function allRozvadecWeldingComplete(metadata: Record<string, unknown> | null | undefined): boolean {
  const flags = parseRozvadecMilestonesFromMetadata(metadata);
  return ROZVADEC_REQUIRED_MILESTONES.every((id) => isRozvadecMilestoneComplete(id, flags));
}
